import _, {isUndefined} from 'lodash';

import {isTracker, teammateCanAccessInbox} from '../../../helpers/modelHelpers';
import {
  buildConversationUpdater,
  bumpConversationTracker,
  ConversationInboxIdsFinder,
  ConversationUpdater,
  setConversationTrackerStage,
  setConversationTrackerStatus,
} from '../../../helpers/models/conversationHelpers';
import {ConversationTeamStatusesEnum} from '../../../models/api/conversationCommonTypes';
import {ConversationPatch} from '../../../models/api/conversationPatchModel';
import {
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../../models/api/conversationTrackerModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {ConversationUndoBannerTypesEnum} from '../../panels/app/banners/bannersState';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {InboxesParameters} from '../../panels/inboxes/parameters/inboxesParameters';
import {getConversations} from '../dataSelectors';
import {getInboxes} from '../inboxes/inboxesSelectors';
import {updateConversationsBulkAction} from './bulkConversationsActions';
import {maybeHideConversationAction} from './conversationsActions';
import {updateConversationsAction} from './updateConversationsActions';

export function canAssignToTeammateAction(
  conversationIds: ReadonlyArray<number>,
  teammate: TeammateModel,
  findConversationInboxIds: ConversationInboxIdsFinder,
): ThunkAction<boolean> {
  return (_dispatch, getState) => {
    const state = getState();
    const inboxes = getInboxes(state);

    // Check if this teammate has inbox access for all of them.
    const hasInboxAccess = _(conversationIds).every((conversationId) =>
      _(findConversationInboxIds(conversationId) ?? [])
        .map((id) => inboxes[id])
        .compact()
        .some((inbox) => teammateCanAccessInbox(teammate, inbox)),
    );

    return hasInboxAccess;
  };
}

interface AssignToTeammateOptionsBase {
  cellsContext?: CellsContext;
  preventUndo?: boolean;
  shouldUnarchive?: boolean;
}

interface AssignToTeammateWithoutContextOptions extends AssignToTeammateOptionsBase {
  cellsContext?: undefined;
  /** This requires a cells context. */
  shouldUnarchive?: false;
}

interface AssignToTeammateWithContextOptions extends AssignToTeammateOptionsBase {
  cellsContext: CellsContext;
  /** When you assign to another teammate, it will archive for you. When you undo that action, we may want to reopen. */
  shouldUnarchive?: boolean;
}

type AssignToTeammateOptions = AssignToTeammateWithoutContextOptions | AssignToTeammateWithContextOptions;

export function assignToTeammateAction(
  conversationIds: ReadonlyArray<number>,
  targetTeammateId: number | undefined,
  options: AssignToTeammateOptions = {},
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {cellsContext, preventUndo} = options;

    const updateOptions = {
      undo: preventUndo
        ? undefined
        : {
            type: ConversationUndoBannerTypesEnum.ASSIGNEE,
          },
    };

    const conversationUpdater = assignConversation(cellsContext, targetTeammateId, options.shouldUnarchive);

    // If we're acting on one conversation only, we may want to hide the conversation.
    if (conversationIds.length === 1) {
      const existingConversation = getConversations(getState())[conversationIds[0]];
      const newConversation = existingConversation && conversationUpdater(existingConversation);

      if (existingConversation && newConversation) {
        dispatch(maybeHideConversationAction(existingConversation, newConversation));
      }
    }

    const teammateIdToUnarchive = options.shouldUnarchive ? options.cellsContext.teammateId : undefined;
    await dispatch(
      updateConversationsAction(
        conversationIds,
        buildAssignConversationPatch(targetTeammateId, teammateIdToUnarchive),
        conversationUpdater,
        updateOptions,
      ),
    );
  };
}

export function assignToTeammateBulkAction(
  inboxesParameters: InboxesParameters,
  cellsContext: CellsContext,
  targetTeammateId?: number,
): ThunkAsyncAction {
  const conversationPatch = buildAssignConversationPatch(targetTeammateId);
  return updateConversationsBulkAction(inboxesParameters, cellsContext, conversationPatch);
}

function buildAssignConversationPatch(
  targetTeammateId: number | undefined,
  teammateIdToUnarchive?: number,
): ConversationPatch {
  // Common patch: set the assignee ID.
  const assigneeId = isUndefined(targetTeammateId) ? null : targetTeammateId;
  const basePatch = {assigneeId};

  // Check if we also need to unarchive.
  if (isUndefined(teammateIdToUnarchive)) {
    return basePatch;
  }

  // If so, update the tracker.
  return {
    ...basePatch,
    updateTrackers: [
      {
        teammateId: teammateIdToUnarchive,
        status: ConversationTrackerStatusesEnum.INBOX,
      },
    ],
  };
}

function assignConversation(
  cellsContext: CellsContext | undefined,
  targetTeammateId: number | undefined,
  shouldUnarchive: boolean | undefined,
): ConversationUpdater {
  const setConversationAssigneeAndStatus: ConversationUpdater = (conversation) => ({
    ...conversation,
    assigneeId: targetTeammateId,
    // Assigning/unassigning always reopens the conversation for the team.
    teamStatus: ConversationTeamStatusesEnum.OPEN,
    overlay: null,
  });

  // Note: we don't have an optimistic update for the previous/former assignee, because it's not noticeable.
  return buildConversationUpdater(
    maybeUpdateAssigneeTracker(targetTeammateId),
    maybeUpdateRequesterTracker(cellsContext, targetTeammateId, shouldUnarchive),
    setConversationAssigneeAndStatus,
  );
}

function maybeUpdateAssigneeTracker(targetTeammateId: number | undefined): ConversationUpdater | undefined {
  // If the conversation is being unassigned, do nothing.
  if (isUndefined(targetTeammateId)) {
    return undefined;
  }

  return buildConversationUpdater(
    bumpConversationTracker(targetTeammateId),
    setConversationTrackerStage(targetTeammateId, ConversationTrackerStagesEnum.ASSIGNEE, undefined),
    setConversationTrackerStatus(targetTeammateId, ConversationTrackerStatusesEnum.INBOX, undefined),
  );
}

function maybeUpdateRequesterTracker(
  cellsContext: CellsContext | undefined,
  targetTeammateId: number | undefined,
  shouldUnarchive: boolean | undefined,
): ConversationUpdater | undefined {
  // If we don't know who the current teammate is, do nothing.
  if (isUndefined(cellsContext)) {
    return undefined;
  }

  const {teammateId} = cellsContext;

  // Reopen the conversation when undoing an assign.
  if (shouldUnarchive) {
    return bumpAndSetConversationTrackerStatusIfExists(teammateId, ConversationTrackerStatusesEnum.INBOX);
  }

  const archiveTracker = bumpAndSetConversationTrackerStatusIfExists(
    teammateId,
    ConversationTrackerStatusesEnum.ARCHIVED,
  );

  // Archive the conversation for you when assigning to another teammate.
  if (!isUndefined(targetTeammateId) && targetTeammateId !== teammateId) {
    return archiveTracker;
  }

  // Archive the conversation for you when unassigning yourself.
  return (conversation) => {
    // If the previous assignee is not the current teammate, do nothing.
    if (conversation.assigneeId !== teammateId) {
      return conversation;
    }

    return archiveTracker(conversation);
  };
}

function bumpAndSetConversationTrackerStatusIfExists(
  teammateId: number,
  status: ConversationTrackerStatusesEnum,
): ConversationUpdater {
  const updateTracker = buildConversationUpdater(
    bumpConversationTracker(teammateId),
    setConversationTrackerStatus(teammateId, status, undefined),
  );

  return (conversation) => {
    // If the current teammate is not already a tracker, do nothing.
    if (!isTracker(conversation.trackers[teammateId])) {
      return conversation;
    }

    return updateTracker(conversation);
  };
}
